import { useState } from 'react';

import type { Property, RowBackground, View } from '@noti/shared';

import {
  boardColumns,
  boardProperty,
  cardDate,
  chipsFor,
  CHIP_COLOR,
  monthGrid,
  monthLabel,
  TITLE_TYPE,
} from './database-rows';
import type { DatabaseRow } from './database-rows';

/**
 * The non-table layouts: board, list, gallery and calendar. Each one is a thin
 * render over the same filtered and sorted rows the table uses; measurements are
 * in `dblock__*` (workspace.css).
 */

export interface LayoutProps {
  rows: DatabaseRow[];
  properties: Property[];
  view: View;
  query: string;
  onOpenRow: (rowId: string) => void;
  onWriteCell: (rowId: string, property: Property, value: string) => void;
  onAddRow: (values: Record<string, string>) => void;
  onRowColor: (rowId: string, color: RowBackground | null) => void;
}

export function BoardView(props: LayoutProps): React.ReactElement {
  const { properties, rows, view, onWriteCell, onAddRow, onOpenRow } = props;
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const groupProp = boardProperty(properties, view.group);
  const columns = boardColumns(rows, properties, view.group);

  return (
    <div className="dblock dblock--board">
      {columns.map((column) => {
        const dropValue =
          column.name === 'All items' ? '' : column.name === 'No value' ? '' : column.name;
        const preset = groupProp ? { [groupProp.id]: dropValue } : {};
        return (
          <div
            key={column.name}
            className={`dboard__col${over === column.name ? ' is-over' : ''}`}
            onDragOver={(event) => {
              if (!dragging) return;
              event.preventDefault();
              setOver(column.name);
            }}
            onDragLeave={() => setOver((current) => (current === column.name ? null : current))}
            onDrop={(event) => {
              event.preventDefault();
              setOver(null);
              setDragging(null);
              if (dragging && groupProp) onWriteCell(dragging, groupProp, dropValue);
            }}
          >
            <div className="dboard__head">
              <span
                className="chip"
                style={{
                  background: CHIP_COLOR[column.chip].bg,
                  color: CHIP_COLOR[column.chip].fg,
                }}
              >
                {column.name}
              </span>
              <span className="dboard__count">{column.rows.length}</span>
              <button
                type="button"
                className="row dboard__add"
                aria-label={`Add item to ${column.name}`}
                onClick={() => onAddRow(preset)}
              >
                +
              </button>
            </div>
            {column.rows.map((row) => (
              <Card
                key={row.id}
                row={row}
                properties={properties}
                view={view}
                onOpen={onOpenRow}
                onDragStart={(event) => {
                  event.dataTransfer.setData('text/plain', row.id);
                  setDragging(row.id);
                }}
              />
            ))}
            <button type="button" className="row dboard__new" onClick={() => onAddRow(preset)}>
              + New
            </button>
          </div>
        );
      })}
    </div>
  );
}

function Card({
  row,
  properties,
  view,
  onOpen,
  onDragStart,
}: {
  row: DatabaseRow;
  properties: Property[];
  view: View;
  onOpen: (rowId: string) => void;
  onDragStart: (event: React.DragEvent) => void;
}): React.ReactElement {
  const chips = chipsFor(row, properties);
  return (
    <div className="dcard" draggable onDragStart={onDragStart} onClick={() => onOpen(row.id)}>
      <span className="dcard__title">{row.title || 'Untitled'}</span>
      <span className="dcard__chips">
        {chips.map((chip) => (
          <span
            key={`${chip.text}-${chip.chip}`}
            className="chip"
            style={{ background: CHIP_COLOR[chip.chip].bg, color: CHIP_COLOR[chip.chip].fg }}
          >
            {chip.text}
          </span>
        ))}
      </span>
      <span className="dcard__date">{cardDate(row, visibleIn(properties, view))}</span>
    </div>
  );
}

function visibleIn(properties: Property[], view: View): Property[] {
  return properties.filter(
    (property) => property.type === TITLE_TYPE || !view.hidden.includes(property.id),
  );
}

export function ListView(props: LayoutProps): React.ReactElement {
  const { rows, properties, view, onOpenRow, onAddRow } = props;
  const shown = visibleIn(properties, view);
  return (
    <div className="dblock dblock--list">
      {rows.map((row) => (
        <button
          key={row.id}
          type="button"
          className="row dlist__row"
          onClick={() => onOpenRow(row.id)}
        >
          <span className="dlist__title">{row.title || 'Untitled'}</span>
          <span className="dlist__chips">
            {chipsFor(row, shown).map((chip) => (
              <span
                key={`${chip.text}-${chip.chip}`}
                className="chip"
                style={{ background: CHIP_COLOR[chip.chip].bg, color: CHIP_COLOR[chip.chip].fg }}
              >
                {chip.text}
              </span>
            ))}
          </span>
          <span className="dlist__date">{cardDate(row, shown)}</span>
        </button>
      ))}
      <button type="button" className="row dlist__new" onClick={() => onAddRow({})}>
        + New
      </button>
    </div>
  );
}

export function GalleryView(props: LayoutProps): React.ReactElement {
  const { rows, properties, view, onOpenRow, onAddRow } = props;
  const shown = visibleIn(properties, view);
  // The prototype paints the cover in the colour of the first select/status
  // option on the row, and falls back to mint.
  const coverProp = properties.find(
    (property) => property.type === 'select' || property.type === 'status',
  );

  return (
    <div className="dblock dblock--gallery">
      {rows.map((row) => {
        const option = coverProp?.options.find(
          (item) => item.n === String(row.values[coverProp.id] ?? ''),
        );
        return (
          <button
            key={row.id}
            type="button"
            className="dgal__card"
            onClick={() => onOpenRow(row.id)}
          >
            <span
              className="dgal__cover"
              style={{
                background: CHIP_COLOR[option?.c ?? 'mint'].bg,
              }}
            />
            <span className="dgal__body">
              <span className="dgal__title">{row.title || 'Untitled'}</span>
              <span className="dgal__chips">
                {chipsFor(row, shown).map((chip) => (
                  <span
                    key={`${chip.text}-${chip.chip}`}
                    className="chip"
                    style={{
                      background: CHIP_COLOR[chip.chip].bg,
                      color: CHIP_COLOR[chip.chip].fg,
                    }}
                  >
                    {chip.text}
                  </span>
                ))}
              </span>
              <span className="dgal__date">{cardDate(row, shown)}</span>
            </span>
          </button>
        );
      })}
      <button type="button" className="row dgal__new" onClick={() => onAddRow({})}>
        + New
      </button>
    </div>
  );
}

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

export function CalendarView(props: LayoutProps): React.ReactElement {
  const { rows, properties, view, onOpenRow, onAddRow } = props;
  const [month, setMonth] = useState(() => {
    const stored = view.month ?? null;
    if (stored) {
      const [year, number] = stored.split('-').map(Number);
      if (year && number) return { year, month: number - 1 };
    }
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const coverProp = properties.find(
    (property) => property.type === 'select' || property.type === 'status',
  );
  const today = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(
    new Date().getDate(),
  ).padStart(2, '0')}`;
  const grid = monthGrid(month.year, month.month, properties, rows);

  const shift = (amount: number) => {
    const next = month.month + amount;
    if (next < 0) setMonth({ year: month.year - 1, month: 11 });
    else if (next > 11) setMonth({ year: month.year + 1, month: 0 });
    else setMonth({ ...month, month: next });
  };

  return (
    <div className="dblock dblock--calendar">
      <div className="dcal__bar">
        <span className="dcal__label">{monthLabel(month.year, month.month)}</span>
        <button
          type="button"
          className="row dcal__nav"
          aria-label="Previous month"
          onClick={() => shift(-1)}
        >
          ‹
        </button>
        <button
          type="button"
          className="row dcal__today"
          onClick={() => {
            const now = new Date();
            setMonth({ year: now.getFullYear(), month: now.getMonth() });
          }}
        >
          Today
        </button>
        <button
          type="button"
          className="row dcal__nav"
          aria-label="Next month"
          onClick={() => shift(1)}
        >
          ›
        </button>
      </div>
      <div className="dcal__grid">
        {WEEKDAYS.map((day) => (
          <div className="dcal__dow" key={day}>
            {day}
          </div>
        ))}
        {grid.map((cell) => (
          <div
            className={cell.inMonth ? 'dcal__cell' : 'dcal__cell dcal__cell--outside'}
            key={cell.iso}
          >
            <div className="dcal__cellhead">
              <span className={cell.iso === today ? 'dcal__num is-today' : 'dcal__num'}>
                {cell.inMonth ? cell.day : ''}
              </span>
              {cell.inMonth ? (
                <button
                  type="button"
                  className="row dcal__add"
                  aria-label={`Add item on ${cell.iso}`}
                  onClick={() => {
                    const dateProp = properties.find((property) => property.type === 'date');
                    if (dateProp) onAddRow({ [dateProp.id]: cell.iso });
                  }}
                >
                  +
                </button>
              ) : null}
            </div>
            {cell.rows.map((row) => {
              const option = coverProp?.options.find(
                (item) => item.n === String(row.values[coverProp.id] ?? ''),
              );
              const chip = CHIP_COLOR[option?.c ?? 'mint'];
              return (
                <button
                  key={row.id}
                  type="button"
                  className="dcal__item"
                  style={{ background: chip.bg, color: chip.fg }}
                  onClick={() => onOpenRow(row.id)}
                >
                  {row.title || 'Untitled'}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Picks the renderer for the active layout; the table is rendered by its parent. */
export function LayoutView({
  layout,
  ...props
}: LayoutProps & { layout: 'board' | 'calendar' | 'list' | 'gallery' }): React.ReactElement {
  if (layout === 'board') return <BoardView {...props} />;
  if (layout === 'calendar') return <CalendarView {...props} />;
  if (layout === 'gallery') return <GalleryView {...props} />;
  return <ListView {...props} />;
}
